import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { creationType, GenericStoreType } from '../stores/generic.store';
import { GenericEntity } from '../types/generic-entity.interface';

type CreatableStore<T extends GenericEntity> = GenericStoreType<T> & {
  create: (entity: creationType<T>) => Promise<T | null>;
};

export interface FieldMapping {
  typeIdField?: string;
  typeNameField?: string;
}

export interface EntityGeneratorConfig<T extends GenericEntity, C extends GenericEntity = GenericEntity> {
  parentGenerator?: (index?: number) => Partial<T>;

  childGenerator?: (parent: T, index?: number, types?: readonly GenericEntity[]) => Partial<C>;

  hasChildrenField?: keyof T;

  childParentIdField?: string;

  fieldMappings?: Record<string, FieldMapping>;
}

@Injectable({
  providedIn: 'root',
})
export class EntityGeneratorService {
  private readonly translate = inject(TranslocoService);

  async generateRandomEntity<T extends GenericEntity>(
    entityType: string,
    parentStore: CreatableStore<T>,
    config: EntityGeneratorConfig<T> = {},
  ): Promise<T | null> {
    try {
      const randomIndex = Math.floor(Math.random() * 1000);
      const baseEntityData: Partial<T> = {
        name: `${entityType} Test ${randomIndex}`,
      } as Partial<T>;

      const entityData: Partial<T> = config.parentGenerator
        ? { ...baseEntityData, ...config.parentGenerator(randomIndex) }
        : baseEntityData;

      if (config.hasChildrenField) {
        entityData[config.hasChildrenField] = true as T[keyof T];
      }

      return await parentStore.create(entityData);
    } catch (error) {
      console.error(this.translate.translate('common.generator.randomEntityError', { type: entityType }), error);
      return null;
    }
  }

  async generateRandomChildEntities<T extends GenericEntity, C extends GenericEntity>(
    parentEntity: T,
    childEntityType: string,
    childStore: CreatableStore<C>,
    count: number = 3,
    config: EntityGeneratorConfig<T, C> = {},
    typeStore?: GenericStoreType<GenericEntity>,
  ): Promise<void> {
    if (!parentEntity || !childStore) return;

    const parentIdField = config.childParentIdField ?? 'parentId';

    try {
      const types: readonly GenericEntity[] = typeStore && typeof typeStore.entities === 'function' ? typeStore.entities() : [];

      for (let i = 0; i < count; i++) {
        const randomIndex = Math.floor(Math.random() * 1000);

        const baseChildData: Record<string, unknown> = {
          name: `${childEntityType} ${randomIndex}`,
          [parentIdField]: parentEntity.id,
        };

        if (types.length > 0) {
          const randomType = types[Math.floor(Math.random() * types.length)];

          if (randomType) {
            const mapping = config.fieldMappings?.[childEntityType];

            if (mapping) {
              if (mapping.typeIdField) {
                baseChildData[mapping.typeIdField] = randomType.id;
              }
              if (mapping.typeNameField) {
                baseChildData[mapping.typeNameField] = randomType.name;
              }
            } else if (childEntityType === 'Asset') {
              baseChildData['assetTypeId'] = randomType.id;
              baseChildData['type'] = randomType.name;
            } else {
              baseChildData['typeId'] = randomType.id;
              baseChildData['type'] = randomType.name;
            }
          }
        }

        const childData: Record<string, unknown> = config.childGenerator
          ? { ...baseChildData, ...(config.childGenerator(parentEntity, i, types) as Record<string, unknown>) }
          : baseChildData;

        await childStore.create(childData as creationType<C>);
      }
    } catch (error) {
      console.error(this.translate.translate('common.generator.randomChildrenError', { name: parentEntity.name }), error);
    }
  }

  async generateRandomEntityWithChildren<T extends GenericEntity, C extends GenericEntity>(
    entityType: string,
    childEntityType: string,
    parentStore: CreatableStore<T>,
    childStore: CreatableStore<C>,
    childCount: number = 3,
    config: EntityGeneratorConfig<T, C> = {},
    typeStore?: GenericStoreType<GenericEntity>,
  ): Promise<T | null> {
    const parent = await this.generateRandomEntity(entityType, parentStore, config);

    if (parent) {
      await this.generateRandomChildEntities(parent, childEntityType, childStore, childCount, config, typeStore);

      return parent;
    }

    return null;
  }
}
